import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { SettingArea, SettingChangePage } from '@restaurant/api-client';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export const PAGE_SIZE = 10;

export interface SettingChangeFilters {
  area?: SettingArea;
  actorUserId?: string;
  /** ISO date-times bounding `changedAt` — `from` inclusive, `to` exclusive. */
  from?: string;
  to?: string;
}

/**
 * One numbered page of the settings history, newest first.
 *
 * `keepPreviousData` holds the current page on screen while the next one loads,
 * so paging doesn't collapse the table to a spinner and bounce the scroll
 * position.
 */
export function useSettingChanges(filters: SettingChangeFilters, page: number) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);

  return useQuery({
    queryKey: ['setting-changes', restaurantId, filters, page],
    queryFn: async () => {
      if (!restaurantId) throw new Error('No restaurant selected');
      return unwrapResult<SettingChangePage>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/setting-changes', {
          params: {
            path: { restaurantId },
            query: { ...filters, page, pageSize: PAGE_SIZE },
          },
        }),
      );
    },
    enabled: !!restaurantId,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}
